import React, { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip } from "@/components/common/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

import { Button } from "components/common/Button";
import { TooltipButton } from "components/common/TooltipButton";
import { CopyButton } from "components/common/CopyButton";
import { InlineError } from "components/common/InlineError";
import { SegmentedTabs } from "components/common/SegmentedTabs";
import { TextInput } from "components/common/TextInput";
import {
  connectBucket,
  getFederationConfig,
  type ConnectBucketProvider,
  type ConnectBucketRequest,
  type ConnectBucketResponse,
  type ConnectGcsAuth,
} from "components/canvas/leftSide/import/Bucket/bucketApi";
import type { BucketScope } from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";
import { useMintOrgId } from "components/main/connections/useMintOrg";
import { useConnectionsStore } from "stores/connectionsStore";
import { useAddGoogleConnection } from "components/main/connections/useGoogleOauthPopup";

const GCP_SETUP_DOCS_URL =
  "https://sambaclab.github.io/mycorr-docs/connections/cloud_storage/gcp_setup.html";

const MAX_NAME_LEN = 64;

interface Props {
  onSaved: (saved: ConnectBucketResponse) => void;
  onCancel: () => void;
  // Hide the in-form Cancel button — used by NewConnectionPanel, which
  // owns its own back arrow in the panel header.
  hideCancel?: boolean;
}

// Auth mechanism. `direct` is the fast path (user's OAuth token reads GCS,
// bucket-scoped); `federation` is WIF (account scope possible, more setup).
type GcsMethod = "direct" | "federation";

interface FormState {
  display_name: string;
  method: GcsMethod;
  // Empty = account scope; non-empty = bucket scope.
  bucket: string;
  // Indicates whether the user explicitly switched to account scope. The
  // `bucket` field stays editable but is sent as ConnectionScope::Account
  // when this flag is true. Account scope is federation-only — OAuth-direct is
  // always bucket-scoped.
  account_scope: boolean;
  gcs: ConnectGcsFormFields;
}

interface ConnectGcsFormFields {
  /// Filled by the Sign-in-with-Google popup callback — the user's Google
  /// OAuth saved-connection backing the Workload Identity id-token.
  google_saved_connection_id: string;
  /// Confirmation string shown to the user after OAuth completes.
  google_account_email: string;
  /// WIF audience from the customer's provider.
  audience: string;
}

const emptyForm = (): FormState => ({
  display_name: "",
  method: "direct",
  bucket: "",
  account_scope: false,
  gcs: {
    google_saved_connection_id: "",
    google_account_email: "",
    audience: "",
  },
});

/// Form for creating a new bucket connection. Two-step inside the form:
/// pick provider → fill credentials. Provider switches preserve all
/// fields so the user doesn't lose work when comparing options.
export const BucketConnectionEditor: React.FC<Props> = ({
  onSaved,
  onCancel,
  hideCancel = false,
}) => {
  const mintOrgId = useMintOrgId();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refetch = useConnectionsStore((s) => s.refetch);

  const update = (patch: Partial<FormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async () => {
    setPending(true);
    setError(null);
    try {
      const provider = buildProviderPayload(form);
      // Account scope is federation-only; OAuth-direct is always bucket-scoped.
      const useAccountScope =
        form.method === "federation" && form.account_scope;
      const scope: BucketScope = useAccountScope
        ? { kind: "account" }
        : { kind: "bucket", name: form.bucket.trim() };
      const req: ConnectBucketRequest = {
        display_name: form.display_name.trim(),
        scope,
        provider,
        org_id: mintOrgId,
      };
      const saved = await connectBucket(req);
      // Mirror the connections store's optimistic insert by triggering a
      // refetch — the bucket connect response shape differs from
      // /api/saved-connections, so we can't pre-patch the cached list
      // with the same object.
      await refetch();
      onSaved(saved);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to save bucket connection",
      );
    } finally {
      setPending(false);
    }
  };

  const ready = isFormReady(form);

  return (
    <div className="flex flex-col gap-5">
      {error && <InlineError>{error}</InlineError>}

      <div className="flex flex-col gap-3">
        <Field
          label="Name"
          value={form.display_name}
          onChange={(v) => update({ display_name: v.slice(0, MAX_NAME_LEN) })}
          placeholder="Prod data lake"
        />
        {form.method === "federation" && (
          <SegmentedTabs
            label="Access scope"
            options={SCOPE_OPTIONS}
            value={form.account_scope ? "account" : "bucket"}
            onChange={(v) => update({ account_scope: v === "account" })}
          />
        )}
        {(form.method === "direct" || !form.account_scope) && (
          <Field
            label="Bucket name"
            value={form.bucket}
            onChange={(v) => update({ bucket: v })}
            placeholder="my-bucket"
          />
        )}
      </div>

      <Section title="Authentication">
        <SegmentedTabs
          label="Method"
          options={METHOD_OPTIONS}
          value={form.method}
          onChange={(v) => update({ method: v as GcsMethod })}
        />
        <GcsFields
          method={form.method}
          fields={form.gcs}
          onChange={(gcs) => update({ gcs })}
        />
      </Section>

      <div className="flex justify-end gap-2">
        <div className="flex gap-2 pt-3">
          {!hideCancel && (
            <Button
              variant="secondary"
              size="md"
              onClick={onCancel}
              disabled={pending}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            size="md"
            loading={pending}
            disabled={!ready}
            onClick={handleSubmit}
          >
            Test &amp; save
          </Button>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Shared form primitives
// ----------------------------------------------------------------------------

const Section: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="flex flex-col gap-3">
    <span className="text-fg-semibold">{title}</span>
    <div className="flex flex-col gap-3">{children}</div>
  </div>
);

const SCOPE_OPTIONS = [
  {
    value: "bucket",
    label: "Single bucket",
    tooltip: "MyCorr only reads this one bucket. Recommended.",
  },
  {
    value: "account",
    label: "Entire project",
    tooltip: "MyCorr can read every bucket in your project.",
  },
] as const;

const METHOD_OPTIONS = [
  {
    value: "direct",
    label: "Sign in with Google",
    tooltip:
      "Fastest. Reads buckets you can access with your own Google account. One specific bucket per connection.",
  },
  {
    value: "federation",
    label: "Workload Identity",
    tooltip:
      "For org/service accounts. Your GCP admin federates MyCorr against a Workload Identity provider. Supports whole-project access.",
  },
] as const;

/** Compact signed-in state + the pre-signed-in primary button. The OAuth
 *  popup mints the Google identity that Workload Identity Federation exchanges,
 *  so this component holds the shared affordance and only exposes `onSignIn`
 *  + state to callers. */
const GoogleSignInControl: React.FC<{
  signedIn: boolean;
  pending: boolean;
  error: string | null;
  onSignIn: () => void;
  accountEmail: string;
}> = ({ signedIn, pending, error, onSignIn, accountEmail }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <span className="text-fg-muted-medium">Google account</span>
    {signedIn ? (
      <div className="inline-flex items-center gap-2 h-8 pl-3 pr-1 rounded-app border border-border bg-bg-subtle self-start min-w-0">
        <span
          aria-hidden="true"
          className="inline-block w-1.5 h-1.5 rounded-full bg-success shrink-0"
        />
        <span className="text-fg-muted-regular truncate">
          {accountEmail || "Signed in to Google"}
        </span>
        <TooltipButton
          title={pending ? "Working…" : "Switch Google account"}
          variant="default"
          size="xs"
          onClick={onSignIn}
          disabled={pending}
          aria-label="Switch Google account"
        >
          <SwapHorizIcon sx={{ fontSize: 12 }} />
        </TooltipButton>
      </div>
    ) : (
      <Button
        variant="primary"
        size="md"
        onClick={onSignIn}
        loading={pending}
        disabled={pending}
      >
        Sign in with Google
      </Button>
    )}
    {error && <InlineError className="mt-1">{error}</InlineError>}
  </div>
);

/** Quiet inline link to the public docs page that walks the customer's
 *  GCP admin through the setup. Replaces the in-form gcloud-step
 *  disclosure: keeping the long-form instructions in one place avoids
 *  drift between the form copy and the docs. The "what this is for"
 *  context lives on the field-label (i) tooltip just above. */
const SetupLink: React.FC = () => (
  <a
    href={GCP_SETUP_DOCS_URL}
    target="_blank"
    rel="noreferrer noopener"
    className="text-fg-muted-regular-small inline-flex items-center gap-1 hover:text-fg w-fit"
  >
    Open the GCP setup guide
    <OpenInNewIcon sx={{ fontSize: 12 }} />
  </a>
);

// ----------------------------------------------------------------------------
// Per-provider field groups
// ----------------------------------------------------------------------------

/** Body of the Authentication section. Owns the shared Google OAuth
 *  flow (called once at this level — both methods consume the same
 *  `google_saved_connection_id`) and renders: sign-in → method
 *  segmented control → per-method credential subform. */
const GcsFields: React.FC<{
  method: GcsMethod;
  fields: ConnectGcsFormFields;
  onChange: (next: ConnectGcsFormFields) => void;
}> = ({ method, fields, onChange }) => {
  const set = (patch: Partial<ConnectGcsFormFields>) =>
    onChange({ ...fields, ...patch });
  const { start: startOauth, pending: oauthPending } = useAddGoogleConnection();
  const [oauthError, setOauthError] = useState<string | null>(null);

  const signIn = async () => {
    setOauthError(null);
    // One unified bucket scope set (openid + devstorage.read_only + identity)
    // serves both mechanisms, so a single sign-in works whether the user picks
    // Direct or Federation — toggling never invalidates the connection.
    const outcome = await startOauth({ clientType: "bucket" });
    if (outcome.status === "ok") {
      set({
        google_saved_connection_id: outcome.saved_connection_id,
        google_account_email: outcome.account_email,
      });
    } else if (outcome.status === "popup_blocked") {
      setOauthError(
        "Browser blocked the popup — allow popups for this site and try again.",
      );
    } else if (outcome.status === "email_mismatch") {
      setOauthError(
        `Google account didn't match the existing connection (expected ${outcome.expected}, got ${outcome.received}).`,
      );
    } else if (outcome.status === "cancelled") {
      setOauthError("Sign-in cancelled — click Sign in with Google to retry.");
    } else if (outcome.status === "error") {
      setOauthError(outcome.message);
    }
  };

  const signedIn = fields.google_saved_connection_id.length > 0;

  return (
    <div className="flex flex-col gap-3">
      <GoogleSignInControl
        signedIn={signedIn}
        pending={oauthPending}
        error={oauthError}
        onSignIn={signIn}
        accountEmail={fields.google_account_email}
      />
      {method === "direct" ? (
        <span className="text-fg-muted-regular-small">
          Sign-in grants MyCorr read-only access to Cloud Storage buckets your
          Google account can already read. No GCP admin setup required.
        </span>
      ) : (
        <GcsFederationCredentials fields={fields} set={set} />
      )}
    </div>
  );
};

type ClientIdStatus = "loading" | "ready" | "error";

const useFederationClientId = (): {
  clientId: string | null;
  status: ClientIdStatus;
  reload: () => void;
} => {
  const [clientId, setClientId] = useState<string | null>(null);
  const [status, setStatus] = useState<ClientIdStatus>("loading");
  // Monotonic request id — bumped on each load and on unmount so a stale
  // in-flight response never writes state for a superseded/cancelled fetch.
  const reqRef = useRef(0);

  const reload = useCallback(() => {
    const req = ++reqRef.current;
    setStatus("loading");
    getFederationConfig()
      .then((cfg) => {
        if (req !== reqRef.current) return;
        // An empty audience means the deployment has no client_id configured
        // — surface it as an error, not a blank box.
        if (cfg.allowed_audience) {
          setClientId(cfg.allowed_audience);
          setStatus("ready");
        } else {
          setStatus("error");
        }
      })
      .catch(() => {
        if (req === reqRef.current) setStatus("error");
      });
  }, []);

  useEffect(() => {
    reload();
    // Capture the stable ref object so the cleanup invalidates whatever
    // request is in flight at unmount (bumping the counter discards its result).
    const ref = reqRef;
    return () => {
      ref.current++;
    };
  }, [reload]);

  return { clientId, status, reload };
};

const ClientIdField: React.FC = () => {
  const { clientId, status, reload } = useFederationClientId();
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="text-fg-muted-medium inline-flex items-center gap-1">
        MyCorr client ID
        <Tooltip title="Hand this to your GCP admin — it's the value for the Workload Identity provider's --allowed-audiences flag. Google stamps it as the audience on your sign-in token.">
          <InfoOutlinedIcon
            sx={{ fontSize: 14 }}
            className="text-fg-muted-medium cursor-help"
          />
        </Tooltip>
      </span>
      <div className="inline-flex items-center gap-2 h-8 pl-3 pr-1 rounded-app border border-border bg-bg-subtle min-w-0">
        {status === "error" ? (
          <>
            <span className="text-fg-muted-regular truncate">
              Couldn't load — try reloading.
            </span>
            <TooltipButton
              title="Reload client ID"
              variant="default"
              size="xs"
              onClick={reload}
              aria-label="Reload MyCorr client ID"
            >
              <RefreshIcon sx={{ fontSize: 14 }} />
            </TooltipButton>
          </>
        ) : (
          <>
            <span className="text-fg-muted-regular truncate font-mono">
              {status === "loading" ? "Loading…" : clientId}
            </span>
            {status === "ready" && clientId && (
              <CopyButton text={clientId} ariaLabel="Copy MyCorr client ID" />
            )}
          </>
        )}
      </div>
    </div>
  );
};

/** Credential subform for the federation method — the MyCorr client_id the
 *  admin needs, the WIF audience input, plus a link to the public GCP setup
 *  doc. Sign-in lives in the parent `GcsFields`. */
const GcsFederationCredentials: React.FC<{
  fields: ConnectGcsFormFields;
  set: (patch: Partial<ConnectGcsFormFields>) => void;
}> = ({ fields, set }) => (
  <>
    <ClientIdField />
    <Field
      label="WIF audience"
      info="The Workload Identity Provider your GCP admin configures to trust your Google sign-in. They'll send you the full audience string (starts with //iam.googleapis.com/…) to paste here."
      value={fields.audience}
      onChange={(v) => set({ audience: v })}
      placeholder="//iam.googleapis.com/projects/123/locations/global/workloadIdentityPools/mycorr-pool/providers/mycorr-issuer"
    />
    <SetupLink />
  </>
);

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: "text" | "password" | "number";
  caption?: React.ReactNode;
  /** Short explanation shown in a tooltip on the (i) icon next to the
   *  label — what the field is for, where the value comes from. */
  info?: string;
}> = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  caption,
  info,
}) => (
  <label className="flex flex-col gap-1 min-w-0">
    <span className="text-fg-muted-medium inline-flex items-center gap-1">
      {label}
      {info && (
        <Tooltip title={info}>
          <InfoOutlinedIcon
            sx={{ fontSize: 14 }}
            className="text-fg-muted-medium cursor-help"
          />
        </Tooltip>
      )}
    </span>
    <TextInput
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete="off"
    />
    {caption && <span className="text-fg-muted-regular-small">{caption}</span>}
  </label>
);

// ----------------------------------------------------------------------------
// Validation + payload assembly
// ----------------------------------------------------------------------------

function isFormReady(form: FormState): boolean {
  if (form.display_name.trim().length === 0) return false;
  if (form.gcs.google_saved_connection_id.length === 0) return false;
  if (form.method === "direct") {
    // Always bucket-scoped: a bucket name is required.
    return form.bucket.trim().length > 0;
  }
  // Federation: bucket name required unless account-scoped; audience required.
  if (!form.account_scope && form.bucket.trim().length === 0) return false;
  return form.gcs.audience.trim().length > 0;
}

function buildProviderPayload(form: FormState): ConnectBucketProvider {
  const auth: ConnectGcsAuth =
    form.method === "direct"
      ? {
          kind: "user_oauth_direct",
          google_saved_connection_id: form.gcs.google_saved_connection_id,
        }
      : {
          kind: "user_oauth_federation",
          audience: form.gcs.audience.trim(),
          google_saved_connection_id: form.gcs.google_saved_connection_id,
        };
  return { provider: "gcs", auth };
}
